import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { getQuery } from "@/service/http.js";
import { ListItem, ConfirmModal } from "../common/index.jsx";
import DealCard from "../deal-card/index.jsx";
import buyDealAPI from "@/service/buy-deal";
import orderAPI from "@/service/order.js";
import { goLogin } from "@/service/utils";
import { PaySelector } from "../deal-payment/index.jsx";
import { VoucherSelector } from "../deal-voucher/index.jsx";
import DealPrivacy from "../deal-privacy.jsx";
import "./index.less";

export default function Cashier(props) {
  const { t } = useTranslation();
  const {
    project,
    restaurant_id,
    deal_id,
    showBack = false,
    onBack,
    baseUrl,
    callback,
    enable_pre_pay,
  } = props;
  const payRef = useRef(null);
  const [qty, setQty] = useState(1);
  const [voucherCodes, setVoucherCodes] = useState([]);
  const [payMethod, setPayMethod] = useState({});
  const [paymentList, setPaymentList] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState({});
  const [orderInfo, setOrderInfo] = useState({});
  const [showMaxDealSizeModal, setShowMaxDealSizeModal] = useState(false);
  const [deal, setDeal] = useState(null);
  let { private_token } = getQuery();

  const max = useMemo(() => {
    if (!deal) return 0;
    return deal.inventory;
  }, [deal]);

  useEffect(() => {
    if (enable_pre_pay) {
      getPaymentList();
    }
    orderAPI.getDealById(deal_id).then((res) => {
      if (!res) return;
      setDeal(res);
    });
  }, [enable_pre_pay]);

  function payCallback(status) {
    callback(status, orderInfo);
  }

  /**
   * 获取支付方式列表
   * @returns
   */
  function getPaymentList() {
    return buyDealAPI
      .getPaymentMethods({
        restaurant_id: restaurant_id,
        project: project,
      })
      .then(setPaymentList);
  }

  /**
   * 点击立即支付，先创建订单，再唤起支付，当支付完成，取消，失败后，进入订单待支付或成功页面
   */
  const createOrder = () => {
    // 超出购买限制
    if (qty > max) {
      return setShowMaxDealSizeModal(true);
    }
    // 需要先登录
    if (!private_token) {
      return goLogin();
    }
    // 需要支付，但是没选择付款方式
    if (enable_pre_pay && !payMethod.keyword) return;
    orderAPI
      .createOrder({
        source: getQuery().source,
        restaurant_id: restaurant_id,
        voucher_codes: voucherCodes,
        deal_id: deal_id,
        project: project,
        quantity: qty,
      })
      .then((res) => {
        setOrderInfo(res);
        if (enable_pre_pay) {
          payRef.current.callPay(res.payment.uuid);
        } else {
          callback("successed", res);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  /**
   * 当优惠券和数量改变，重新计算订单金额
   */
  useEffect(() => {
    if(!enable_pre_pay) return;
    buyDealAPI
      .getPaymentInfo({
        restaurantId: restaurant_id,
        vouchers: voucherCodes,
        deal_id: deal_id,
        project: project,
        number: qty,
      })
      .then((res) => {
        setPaymentInfo(res);
      });
  }, [voucherCodes, qty, enable_pre_pay]);

  return (
    <div className="buydeal-cashier buydeal-popup">
      {showMaxDealSizeModal && (
        <ConfirmModal
          onCancel={() => setShowMaxDealSizeModal(false)}
          onOk={() => setShowMaxDealSizeModal(false)}
          okText={t("walkin.Got it")}
        >
          {t("walkin.You can select up", { size: max })}
        </ConfirmModal>
      )}
      <div className="buydeal-popup-header">
        <div className="header-left" onClick={onBack}>
          {showBack && <i className="icon icon-arrow-left"></i>}
        </div>
        <strong>
          {enable_pre_pay ? t("walkin.Buy now") : t("walkin.Claim now")}
        </strong>
        <span className="header-right"></span>
      </div>
      <div className="buydeal-popup-line" hidden={!props.isPc}></div>
      <div className="buydeal-popup-body">
        <div className="buydeal-popup-title">{t("WEB.Deal")}</div>
        {/* 套餐卡片 */}
        {!!deal && (
          <DealCard
            deal={deal}
            showHandler={true}
            qty={qty}
            max={max}
            setQty={setQty}
            onLimit={() => setShowMaxDealSizeModal(true)}
          ></DealCard>
        )}

        {/* 支付方式 */}
        {enable_pre_pay && (
          <PaySelector
            baseUrl={baseUrl}
            payRef={payRef}
            payMethod={payMethod}
            setPayMethod={setPayMethod}
            paymentInfo={paymentInfo}
            paymentMethods={paymentList}
            orderInfo={orderInfo}
            payCallback={payCallback}
          ></PaySelector>
        )}

        {/* 优惠券 */}
        {enable_pre_pay && (
          <VoucherSelector
            project={project}
            deal_id={deal_id}
            restaurant_id={restaurant_id}
            voucherCodes={voucherCodes}
            setVoucherCodes={setVoucherCodes}
          ></VoucherSelector>
        )}

        <div className="buydeal-popup-line" style={{ margin: 0 }}></div>
        {/* 价格 */}
        <ListItem
          title={t("walkin.Subtotal")}
          hidden={!enable_pre_pay}
          value={
            <span style={{ fontWeight: 700 }}>{paymentInfo.format_amount}</span>
          }
        ></ListItem>
        {!enable_pre_pay && <div style={{height: 16}}></div>}
        {/* 条款 */}
        <DealPrivacy
          buttonText={
            enable_pre_pay ? t("PAYMENT.Pay now") : t("walkin.Claim now")
          }
        ></DealPrivacy>
      </div>
      <div className="buydeal-popup-footer">
        {enable_pre_pay && (
          <div className="buydeal-cashier-amount">
            <div className="amount-now">{paymentInfo.format_amount}</div>
            {voucherCodes.length > 0 && (
              <div className="amount-old">
                <span>{t("walkin.Original")}: </span>
                <span style={{ textDecoration: "line-through" }}>
                  {paymentInfo.format_origin_amount}
                </span>
              </div>
            )}
          </div>
        )}

        {showBack && (
          <div className="buydeal-popup-btn" onClick={onBack}>
            {t("CANCEL")}
          </div>
        )}
        <div
          className={classNames("buydeal-popup-btn primary", {
            disabled: enable_pre_pay ? !payMethod.name : false,
          })}
          onClick={createOrder}
        >
          {enable_pre_pay ? t("PAYMENT.Pay now") : t("walkin.Claim now")}
        </div>
      </div>
    </div>
  );
}
